import { useState } from "react";
import { Plus, Mail, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, PageHeader } from "@/components/Panel";
import { QuoteButton } from "@/components/QuoteDialog";
import { MockupButton } from "@/components/MockupDialog";
import { WebsiteChats } from "@/components/WebsiteChats";
import { http, useResource, emitRefresh, money } from "@/lib/api";

const STAGES = ["new", "contacted", "qualified", "won", "lost"];
const EMPTY = { name: "", company: "", email: "", value: "", notes: "", expected_close: "" };

const NewLead = () => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    await http.post("/leads", { ...f, value: Number(f.value) || 0, expected_close: f.expected_close || null });
    setF(EMPTY);
    toast.success("Lead logged — new-lead workflows triggered");
    emitRefresh();
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_130px_160px_auto] gap-2">
      <input className="hud-input" placeholder="Name" value={f.name} onChange={set("name")} data-testid="lead-name-input" />
      <input className="hud-input" placeholder="Company" value={f.company} onChange={set("company")} data-testid="lead-company-input" />
      <input className="hud-input" placeholder="Email" value={f.email} onChange={set("email")} data-testid="lead-email-input" />
      <input className="hud-input" type="number" placeholder="Value $" value={f.value} onChange={set("value")} data-testid="lead-value-input" />
      <input className="hud-input" type="date" title="Expected close" value={f.expected_close} onChange={set("expected_close")} data-testid="lead-close-input" />
      <button className="hud-btn justify-center" data-testid="lead-add-button"><Plus size={14} /> Add Lead</button>
    </form>
  );
};

const LeadRow = ({ l, onDraft, drafting }) => {
  const stage = (e) => http.patch(`/leads/${l.id}`, { status: e.target.value }).then(emitRefresh);
  return (
    <tr className="border-b border-cyan-900/50 hover:bg-cyan-950/30 transition-colors" data-testid={`lead-row-${l.id}`}>
      <td className="py-3 px-3 border-l-2 border-transparent hover:border-cyan-400">
        <div className="font-semibold text-slate-100">{l.name}</div>
        <div className="font-mono-hud text-[11px] text-slate-500">{l.email}</div>
      </td>
      <td className="py-3 px-3 text-slate-300">{l.company}</td>
      <td className="py-3 px-3 font-display text-cyan-200">{money(l.value)}</td>
      <td className="py-3 px-3">
        <input type="date" className="hud-input !py-1 !w-40 font-mono-hud !text-[12px]" value={l.expected_close || ""} onChange={(e) => http.patch(`/leads/${l.id}`, { expected_close: e.target.value }).then(emitRefresh)} data-testid={`lead-close-${l.id}`} />
      </td>
      <td className="py-3 px-3">
        <select value={l.status} onChange={stage} className="hud-input !py-1 !w-40 uppercase text-xs tracking-wider" data-testid={`lead-status-${l.id}`}>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="py-3 px-3">
        <div className="flex gap-2 justify-end">
          <QuoteButton lead={l} />
          <MockupButton lead={l} testId={`lead-mockup-${l.id}`} />
          <button className="text-slate-500 hover:text-red-300 px-1" onClick={() => http.delete(`/leads/${l.id}`).then(emitRefresh)} data-testid={`lead-delete-${l.id}`}><Trash2 size={15} /></button>
        </div>
      </td>
    </tr>
  );
};

export default function CRM() {
  const [leads] = useResource("/leads");
  const [drafting, setDrafting] = useState(null);
  const [draft, setDraft] = useState(null);
  const onDraft = async (l) => {
    setDrafting(l.id);
    try { const { data } = await http.post(`/leads/${l.id}/followup`); setDraft(data); }
    catch { toast.error("Draft failed"); }
    setDrafting(null);
  };
  return (
    <div>
      <PageHeader code="OMVEXIS://ENQUIRIES" title="Website Enquiries" sub="Buyer enquiries from the omvexis.com Jarvis bubble. JARVIS drafts a priced quote for each one."
        right={<MockupButton testId="mockup-studio-button" label="Mockup studio" />} />
      <WebsiteChats />
      <Panel label={`${leads?.length || 0} enquiries`} title="Website Enquiries" testId="crm-table">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-cyan-500/30">
                {["Contact", "Company", "Value", "Expected Close", "Stage", ""].map((h) => <th key={h} className="hud-label py-2 px-3 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>{(leads || []).map((l) => <LeadRow key={l.id} l={l} onDraft={onDraft} drafting={drafting === l.id} />)}</tbody>
          </table>
        </div>
      </Panel>
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="bg-slate-950 border-cyan-500/40 max-w-2xl" data-testid="followup-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-wider text-cyan-200">Follow-up · {draft?.name}</DialogTitle><DialogDescription className="text-slate-400">AI-drafted follow-up email</DialogDescription></DialogHeader>
          <pre className="whitespace-pre-wrap font-mono-hud text-[13px] text-slate-200 max-h-[60vh] overflow-y-auto hud-scroll" data-testid="followup-draft-text">{draft?.last_draft}</pre>
          <button className="hud-btn self-start" onClick={() => { navigator.clipboard.writeText(draft.last_draft); toast.success("Copied"); }} data-testid="followup-copy-button"><Copy size={14} /> Copy</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
